import { useEffect } from 'react';
import { getUserPermissionsAPI } from '../api/auth.api';
import { useUserStore } from '../store/userStore';
import { Permission, User } from '../types/user.d';
import { extractUserFromJWT, isValidJWT } from '../utils/jwt';
import { getAuthToken } from '../utils/storage';

/**
 * 앱 시작 시 저장소에서 인증 정보를 복원하는 훅
 */
export const useCookieSync = () => {
  const { setAccessToken, setUser, user, accessToken } = useUserStore();

  useEffect(() => {
    // 스토어에 데이터가 이미 있고 권한도 있으면 스킵
    if (
      user &&
      accessToken &&
      user.permissions &&
      user.permissions.length > 0
    ) {
      return;
    }

    const restoreUserData = async () => {
      const storedToken = getAuthToken();

      if (storedToken && isValidJWT(storedToken)) {
        try {
          // 1. 토큰 설정 (JWT에서 기본 사용자 정보 추출)
          setAccessToken(storedToken);

          // 2. 권한 정보를 별도로 조회
          const permissionsData = await getUserPermissionsAPI();

          // 3. JWT에서 기본 사용자 정보 추출
          const basicUser = extractUserFromJWT(storedToken);

          if (basicUser) {
            // 4. 권한 데이터 변환
            const permissions: Permission[] = [];

            // 새로운 최적화된 API 응답 구조 (권한 이름 배열)
            if (
              permissionsData.permissions &&
              Array.isArray(permissionsData.permissions) &&
              typeof permissionsData.permissions[0] === 'string'
            ) {
              permissionsData.permissions.forEach((permName: string) => {
                permissions.push({
                  name: permName,
                  displayName: permName,
                  source: 'api',
                });
              });
            }
            // 레거시: fromRoles에서 권한 추출
            else if (
              permissionsData.permissions?.fromRoles &&
              Array.isArray(permissionsData.permissions.fromRoles)
            ) {
              permissionsData.permissions.fromRoles.forEach((roleData: any) => {
                if (
                  roleData.permissions &&
                  Array.isArray(roleData.permissions)
                ) {
                  roleData.permissions.forEach((perm: any) => {
                    permissions.push({
                      _id: perm._id,
                      name: perm.name,
                      displayName: perm.displayName || perm.name,
                      description: perm.description,
                      domain: perm.domain,
                      action: perm.action,
                      targetType: perm.targetType,
                      source: 'role',
                      roleName: roleData.role?.name,
                    });
                  });
                }
              });
            }

            // 5. 역할 데이터 변환
            let roles: any[] = [];
            if (permissionsData.roles && Array.isArray(permissionsData.roles)) {
              // 새로운 API 구조: 역할 이름 배열
              if (typeof permissionsData.roles[0] === 'string') {
                roles = permissionsData.roles.map((roleName: string) => ({
                  name: roleName,
                  displayName: roleName,
                }));
              }
              // 레거시: fromRoles에서 역할 정보 추출
              else if (permissionsData.permissions?.fromRoles) {
                roles = permissionsData.permissions.fromRoles.map(
                  (roleData: any) => ({
                    _id: roleData.role?._id,
                    name: roleData.role?.name,
                    description: roleData.role?.description,
                  })
                );
              }
            }

            // 6. 완전한 사용자 데이터 구성
            const completeUser: User = {
              ...basicUser,
              permissions,
              roles,
            };

            // 7. 사용자 정보 업데이트 (권한 포함)
            setUser(completeUser);
          }
        } catch (error) {
          // 권한 조회 실패 시에도 기본 사용자 정보는 유지
          // setAccessToken에서 이미 기본 정보는 설정됨
        }
      }
    };

    restoreUserData();
  }, [setAccessToken, setUser]);

  return null;
};

export default useCookieSync;
